import React, { useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import Head from 'next/head';
import useSWR from 'swr';
import dayjs from 'dayjs';
import {
	Flex, Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Heading, Text, Button, Icon, Circle, Tooltip, Link as ChakraLink, Badge, SimpleGrid, List, ListItem,
	Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverArrow, PopoverCloseButton, LinkBox, LinkOverlay,
} from '@chakra-ui/react';
import pangu from 'pangu';
import { FontAwesomeIcon as Fa } from '@fortawesome/react-fontawesome';
import {
	faCopy,
	faDatabase,
	faDownload, faExternalLinkAlt, faEye, faGhost, faBookmark,
} from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons';
import {
	FacebookShareButton as FBShare, TwitterShareButton as TWShare, LineShareButton as LNShare, TelegramShareButton as TGShare,
	FacebookIcon as FBIcon, TwitterIcon as TWIcon, LineIcon as LNIcon, TelegramIcon as TGIcon,
} from 'react-share';
import { NextSeo, BreadcrumbJsonLd } from 'next-seo';
import { jsonLdScriptProps } from 'react-schemaorg';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Page from '../../components/Page';
import { ContextStore } from '../../contexts/authContext';
import { Loading } from '../../components/c';
import axios, { serversideInstance as serverSideAxios } from '../../utils/axios';

export default function Home({ dataset: data, similarDatasets }) {
	const router = useRouter();
	const { userState } = useContext(ContextStore);
	const { data: dataFavorite, mutate: mutateFavorite } = useSWR(userState?.user ? `/api/v2/collection/isFavorite/${router?.query?.id}` : null);

	useEffect(() => {
		if (router?.query?.id && data?.nanoId && router?.query?.id !== data?.nanoId) {
			router.replace(`/dataset/${data?.nanoId}`, null, { shallow: true });
		}
	}, [data, router.query.id]);

	let description = data?.desc ?? '';
	if (description.length < 50) {
		description = `${description} | ${data?.name}`;
	}
	let retry = 50;
	// eslint-disable-next-line no-plusplus
	while (description.length < 50 && retry--) {
		description = `${description}.`;
	}

	const toggleFavorite = async () => {
		if (dataFavorite?.isFavorite) {
			await axios.delete(`/api/v2/collection/favorite/${router?.query?.id}`);
		} else {
			await axios.post(`/api/v2/collection/favorite/${router?.query?.id}`);
		}
		mutateFavorite(!dataFavorite?.isFavorite);
	};

	return (
		<>
			<Page title={`${data?.name ?? '載入中'} | ODPortal 開放資料集散地`}>
				{ data ? (
					<>
						<NextSeo
							title={`${data?.name} | ODPortal 開放資料集散地`}
							description={description.slice(0, 50)}
						/>
						<BreadcrumbJsonLd
							keyOverride={data?.nanoId}
							itemListElements={[
								{
									position: 1,
									name: 'ODPortal',
									item: 'https://odportal.tw',
								},
								{
									position: 2,
									name: data?.government?.name,
									item: `https://odportal.tw/datasets/search?s=${data?.government?.name}`,
								},
								{
									position: 3,
									name: data?.name,
									item: `https://odportal.tw/dataset/${data?.nanoId}`,
								},
							]}
						/>
						<Head>
							<script
								{...jsonLdScriptProps({
									'@context': 'https://schema.org',
									'@type': 'Dataset',
									name: data?.name,
									description,
									url: `https://odportal.tw/dataset/${data?.nanoId}`,
									sameAs: data.url,
									license: 'https://data.gov.tw/license',
									distribution: data.resources ? data.resources.map((r) => ({
										'@type': 'DataDownload',
										encodingFormat: r.format,
										name: (r.name && r.name.length) ? r.name : r.desc,
										description: (r.name && r.name.length) ? r.desc : '',
										contentUrl: `${process.env.NEXT_PUBLIC_CDN_PATH}/api/v1/resource/${data.nanoId}/${r._id}`,
									})) : [],
								})}
							/>
						</Head>
						<Box mt={4}>
							<Breadcrumb fontSize="sm" color="gray.700">
								<BreadcrumbItem>
									<NextLink passHref href="/">
										<BreadcrumbLink data-umami-event="breadcrumb:home">ODPortal</BreadcrumbLink>
									</NextLink>
								</BreadcrumbItem>
								<BreadcrumbItem>
									<BreadcrumbLink href="#" data-umami-event="breadcrumb:gov">{data.government.name}</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbItem>
									<NextLink passHref href={`/dataset/${data._id}`}>
										<BreadcrumbLink data-umami-event="breadcrumb:data">{pangu.spacing(data.name)}</BreadcrumbLink>
									</NextLink>
								</BreadcrumbItem>
							</Breadcrumb>
						</Box>
						<Box mt={6}>
							<Flex my={2} alignItems="center" justifyContent="space-between" flexWrap={['wrap', 'nowrap']}>
								<Heading as="h1" fontWeight="800" fontSize="2xl" display="inline-block">{pangu.spacing(data.name || '')}</Heading>
								{userState?.user ? (
									<Tooltip
										label={dataFavorite?.isFavorite ? '取消收藏' : '加入收藏'}
										placement="top"
									>
										<Button
											minW="120px"
											m={2}
											colorScheme="blackAlpha"
											variant="outline"
											bg="textGray"
											color="white"
											leftIcon={<Fa icon={dataFavorite?.isFavorite ? faBookmark : faBookmarkRegular} />}
											style={{
												textDecoration: 'none',
											}}
											_hover={{
												bg: '#8F99A3',
												color: 'white',
											}}
											_active={{
												bg: '#8F99A3',
												color: 'white',
											}}
											data-umami-event="dataset:favorite"
											onClick={() => toggleFavorite()}
										>
											<Text color="white">
												{dataFavorite?.isFavorite ? '已收藏' : '加入收藏'}
											</Text>
										</Button>
									</Tooltip>
								) : (
									<Button
										minW="120px"
										m={2}
										colorScheme="blackAlpha"
										variant="outline"
										bg="textGray"
										color="white"
										leftIcon={<Fa icon={dataFavorite?.isFavorite ? faBookmark : faBookmarkRegular} />}
										style={{
											textDecoration: 'none',
										}}
										_hover={{
											bg: '#8F99A3',
											color: 'white',
										}}
										_active={{
											bg: '#8F99A3',
											color: 'white',
										}}
										data-umami-event="dataset:favorite-unlogin"
										onClick={() => router.push(`${process.env.NEXT_PUBLIC_AUTH_API_PATH}/login?redirect=${window?.location?.href}`)}
									>
										<Text color="white">
											登入後收藏
										</Text>
									</Button>
								)}

							</Flex>
							<Box fontWeight={200} fontSize="md" py={1} letterSpacing=".5px" lineHeight="160%">
								{pangu.spacing(data.desc || '')}
							</Box>
						</Box>
						<Flex mt={2} p={4} borderRadius={5} bgColor="#E3E3E3">
							<Box w={['100%', '30%']} px={3} borderLeft="2px dotted #333">
								<div>
									資料提供：
									{data.government.name}
								</div>
								<div>
									提供機關：
									{data.provider ? data.provider : <Text as="span" color="#999">無法取得</Text>}
								</div>
								<div>
									建立日期：
									{data.createdAt && !data.createdAt.startsWith('1970-01-01T00:00:00') ? (
										dayjs(data.createdAt).format('YYYY-MM-DD')
									) : (
										<Text as="span" color="#999">未提供上架日期</Text>
									)}
								</div>
							</Box>
							<Box w={['100%', '30%']} px={3} borderLeft="2px dotted #333">
								<div>
									原始資料：
									<a
										target="_blank"
										rel="noreferrer"
										href={data.url}
									>
										{data.government.name}
										{' '}
										<Fa icon={faExternalLinkAlt} />
									</a>
								</div>
								<div>
									主題分類：
									{data.theme ? (
										<a href={`/datasets?theme=${data.theme}`}>{data.theme}</a>
									) : <Text as="span" color="#999">無法取得</Text>}
								</div>
								<div>
									服務分類：
									{data.service ? (
										<a href={`/datasets?service=${data.service}`}>{data.service}</a>
									) : <Text as="span" color="#999">無法取得</Text>}
								</div>
							</Box>
							<Box w={['100%', '30%']} px={3} borderLeft="2px dotted #333" d="none">
								<div>
									資料集版本：
									<Popover>
										<PopoverTrigger>
											<ChakraLink as="button">
												{data.versions.length > 0 ? `${data.versions.length} 份快照` : '最新資料'}
											</ChakraLink>
										</PopoverTrigger>
										<PopoverContent>
											<PopoverArrow />
											<PopoverCloseButton />
											<PopoverHeader>資料集快照版本</PopoverHeader>
											<PopoverBody>
												<List>
													{data.versions.map((v) => (
														<ListItem key={v.timestamp}>
															<ChakraLink href="#">
																{dayjs(v.timestamp).format('YYYY/MM/DD')}
															</ChakraLink>
														</ListItem>
													))}
												</List>
											</PopoverBody>
										</PopoverContent>
									</Popover>
								</div>
							</Box>
						</Flex>
						<Box mt={4} py={2} className="mt-4 py-2">
							<Heading as="h2" fontSize="lg" fontWeight={600} mb={3}>可用資源</Heading>
							{data.resources.length === 0 ? (
								<Box my={3} mx="auto">
									<Box textAlign="center"><Icon as={Fa} color="#999" fontSize="5xl" icon={faGhost} /></Box>
									<Box textAlign="center" mt={3}>
										沒有可用的資源，前往
										{' '}
										<ChakraLink
											target="_blank"
											rel="noreferrer"
											href={data.url}
										>
											原始資料
											{' '}
											<Fa icon={faExternalLinkAlt} />
										</ChakraLink>
										{' '}
										查看備註說明。
									</Box>
								</Box>
							) : null }
							<SimpleGrid columns={[1, 2]} spacing={2} w="100%">
								{data.resources && data.resources.map((r) => (
									<Box>
										<div className="resource / bg-white p-2 border rounded-sm mb-1">
											<div>
												<Badge bgColor="lightblue" size="lg">{r.format}</Badge>
												{' '}
												{(r.name && r.name.length) ? r.name : r.desc}
											</div>
											<div>
												<small>{(r.name && r.name.length) ? r.desc : ''}</small>
											</div>
											<div className="float-right m-2">
												{ (r.format.toUpperCase() === 'CSV' || r.format.toUpperCase() === 'JSON'
													|| r.format.toUpperCase() === 'JSON-LD'
													|| r.format.toUpperCase() === 'XML' || r.format.toUpperCase() === 'PDF') ? (
														<Tooltip label="預覽資料">
															<Box d="inline-block" mx={1}>
																<NextLink href={`/playground/${data.nanoId}?r=${r._id}`}>
																	<Button
																		size="xs"
																		as={ChakraLink}
																		colorScheme="yellow"
																		data-umami-event="dataset:preview"
																		data-umami-event-dataset={data.nanoId}
																	>
																		<Icon as={Fa} icon={faEye} mr={2} />
																		預覽
																	</Button>
																</NextLink>
															</Box>
														</Tooltip>
													) : null}
												<Box d="inline-block" mx={1}>
													<Tooltip label="取得資料">
														<Button
															size="xs"
															as={ChakraLink}
															_hover={{
																textDecor: 'none',
																color: 'inherit',
															}}
															target="_blank"
															rel="noreferrer nofollow"
															href={`${process.env.NEXT_PUBLIC_CDN_PATH}/api/v1/resource/${data.nanoId}/${r._id}`}
															data-umami-event="dataset:download"
															data-umami-event-dataset={data.nanoId}
														>
															<Icon as={Fa} icon={faDownload} mr={2} />
															下載
														</Button>

													</Tooltip>
												</Box>
											</div>
											<div className="clearfix" />
										</div>
									</Box>
								))}
							</SimpleGrid>
						</Box>
						<Box>
							<Heading as="h2" fontSize="lg" fontWeight={600} mb={3}>分享這個開放資料集</Heading>
							<Flex my={2} mb={4}>
								<Box p={3} borderRadius={5} bgColor="#fff" w={['100%', '50%']}>
									<Box borderBottom="1px solid #ccc" py={2} pb={4}>
										<Flex alignItems="center">
											<Box mx={2} mr={4}>
												<Icon as={Fa} fontSize="2xl" color="#333" icon={faDatabase} />
											</Box>
											<Box>
												<Text fontSize="sm" fontWeight={600}>{data.name}</Text>
												<Flex w="100%" justifyContent="space-between" alignItems="center">
													<Text fontWeight={600} fontFamily="Open Sans, sans-serif" color="#555" fontSize="xs">
														odp.tl/
														{data.nanoId}
													</Text>
												</Flex>
											</Box>
										</Flex>
									</Box>
									<Flex mb={2} mt={3} justifyContent="flex-end">
										<FBShare
											url={`${process.env.NEXT_PUBLIC_API_PATH}/dataset/${data.nanoId}`}
											data-umami-event="dataset:share"
											data-umami-event-share="facebook"
											data-umami-event-dataset={data.nanoId}
										>
											<FBIcon size={32} round />
										</FBShare>
										<Box w={2} />
										<TWShare
											url={`${process.env.NEXT_PUBLIC_API_PATH}/dataset/${data.nanoId}`}
											data-umami-event="dataset:share"
											data-umami-event-share="twitter"
											data-umami-event-dataset={data.nanoId}
										>
											<TWIcon size={32} round />
										</TWShare>
										<Box w={2} />
										<TGShare
											url={`${process.env.NEXT_PUBLIC_API_PATH}/dataset/${data.nanoId}`}
											data-umami-event="dataset:share"
											data-umami-event-share="telegram"
											data-umami-event-dataset={data.nanoId}
										>
											<TGIcon size={32} round />
										</TGShare>
										<Box w={2} />
										<LNShare
											url={`${process.env.NEXT_PUBLIC_API_PATH}/dataset/${data.nanoId}`}
											data-umami-event="dataset:share"
											data-umami-event-share="line"
											data-umami-event-dataset={data.nanoId}
										>
											<LNIcon size={32} round />
										</LNShare>
										<Box w={2} />
										<CopyToClipboard
											text={`https://odp.tl/${data.nanoId}`}
											data-umami-event="dataset:share"
											data-umami-event-share="copy"
											data-umami-event-dataset={data.nanoId}
										>
											<Circle as={Button} minW={0} bgColor="pink.600" w="32px" h="32px" colorScheme="pink">
												<Icon as={Fa} fontSize="sm" color="#fff" icon={faCopy} />
											</Circle>
										</CopyToClipboard>
									</Flex>
								</Box>
							</Flex>
						</Box>
						<Box>
							<Heading as="h2" fontSize="lg" fontWeight={600}>你可能有興趣的開放資料</Heading>
							<SimpleGrid columns={2} spacing={5} w="100%" mt={3}>
								{similarDatasets === null ? <Box textAlign="center"><Loading /></Box> : null}
								{similarDatasets && similarDatasets.length === 0 ? <Box textAlign="center"><Text fontSize="sm">沒有資料集</Text></Box> : null}
								{similarDatasets && similarDatasets.filter((x) => x._id !== data._id).slice(0, 6).map((x) => (

									<LinkBox p={5} bgColor="#fafafa" rounded="md" shadow="sm" position="relative" _hover={{ bgColor: '#f7f7f7' }}>
										<Text fontSize="xs" fontWeight={600}>{x?.government?.name}</Text>
										<Text fontSize="sm" position="absolute" top="1rem" right="1rem" color="#8c8c8c">
											{' '}
										</Text>
										<NextLink href={`/dataset/${x.nanoId}`} passHref>
											<LinkOverlay
												_hover={{
													color: 'inherit',
													textDecoration: 'none',
												}}
											>
												<Heading as="h3" fontSize="md" fontWeight={600}>
													{pangu.spacing(x.name || '')}
												</Heading>
											</LinkOverlay>
										</NextLink>
										<Box as="p" color="#484848" my={1}>
											{pangu.spacing(x.desc.slice(0, 100) || '')}
											{x.desc.length > 100 ? '...' : ''}
										</Box>
										<Box>
											{Array.from(new Set(x.resources.map((r) => r.format))).map((f) => (
												<Badge bgColor="lightblue" color="#000" mr={1} key={`${x.datasetId}-${f}`}>{f.toUpperCase()}</Badge>
											))}
										</Box>
									</LinkBox>

									/*
									<Box mt={1} p={4} py={5} border="1px solid #777" borderRadius={5} boxShadow="xl" key={x.datasetId}>
										<div className={`font-weight-bold ${styles.title}`}>
											<NextLink href={`/dataset/${x.nanoId}`} passHref>
												<ChakraLink>
													{pangu.spacing(x.name || '')}
												</ChakraLink>
											</NextLink>
										</div>
										<div>
											<Box d="inline-block" mr={2}>
												{Array.from(new Set(x.resources.map((r) => r.format))).map((f) => (
													<Badge bgColor="lightblue" mr={1} key={`${x.datasetId}-${f}`}>{f.toUpperCase()}</Badge>
												))}
												<small>
													{x.government.name}
													<Box d="inline-block" ml={2}>
														{dayjs(x.createdAt).format('YYYY-MM-DD')}
													</Box>
												</small>
											</Box>
										</div>
										<Box fontWeight={200} fontSize="sm" py={1}>
											{pangu.spacing(x.desc.slice(0, 100) || '')}
											{x.desc.length > 100 ? '……' : ''}
										</Box>
									</Box>
									*/
								))}
							</SimpleGrid>
						</Box>
					</>
				) : <Loading />}
			</Page>
		</>
	);
}

export async function getServerSideProps(ctx) {
	const { id } = ctx.params;

	let dataset;
	try {
		const { data } = await serverSideAxios.get(`/api/v2/dataset/${id}`, {
			headers: ctx.req.headers,
		});
		dataset = data;
	} catch (e) {
		if (e.response.status === 404) {
			return { props: { notFound: true } };
		}
		return { props: { error: true } };
	}

	let similarDatasets = [];
	try {
		const { data: sd } = await serverSideAxios.get(`/api/v2/similar/dataset/${id}?limit=6`);
		similarDatasets = sd.filter((x) => x._score < 1).slice(0, 4);
	} catch (e) {
		similarDatasets = [];
	}

	return {
		props: {
			dataset,
			similarDatasets,
		},
		// revalidate: 600,
	};
}

/*
export async function getStaticPaths() {
	const { data: latestDatasets } = await axios.get('/api/v1/datasets');

	return {
		paths: latestDatasets.map((x) => ({ params: { id: x.nanoId } })),
		fallback: true,
	};
}
*/
